import * as React from "react"
import { ChevronDown, ExternalLink, KeyRound, Loader2, ShieldCheck } from "lucide-react"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useBookingT } from "@/hooks/use-translations"
import { createConnection, HrisApiError, type HrisConnection } from "@/features/integrations/hris/hrisApi"

type Props = {
  provider: string
  providerName: string
  /** Prefilled when replacing the key of an existing connection. */
  tenantUrl?: string
  onConnected: (connection: HrisConnection) => void
  onCancel?: () => void
}

/**
 * Account address + API key. The backend tests the key against the HR system
 * before saving, so a wrong key is reported here, not at the first sync.
 */
export function HrisConnectCard({ provider, providerName, tenantUrl: initialUrl = "", onConnected, onCancel }: Props) {
  const t = useBookingT()
  const [tenantUrl, setTenantUrl] = React.useState(initialUrl)
  const [apiKey, setApiKey] = React.useState("")
  const [saving, setSaving] = React.useState(false)
  const [errors, setErrors] = React.useState<Record<string, string>>({})

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      onConnected(await createConnection({ provider, tenantUrl: tenantUrl.trim(), apiKey: apiKey.trim() }))
    } catch (err) {
      const fields = err instanceof HrisApiError ? err.fields : {}
      setErrors({
        tenantUrl: fields.tenantUrl?.[0] ?? "",
        apiKey: fields.apiKey?.[0] ?? (Object.keys(fields).length === 0 ? (err as Error).message : ""),
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardContent className="p-6">
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-neutral-900">
              {t("hris_connect_title", "Connect {name}").replace("{name}", providerName)}
            </h2>
            <p className="max-w-2xl text-sm text-neutral-800">
              {t(
                "hris_connect_intro",
                "Employees, companies, departments and the traveller data airlines require are kept in sync from your HR system. Nothing changes in Travel Code until you have reviewed a preview and switched the sync on.",
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="hris-tenant">{t("hris_tenant_label", "Account address")}</Label>
              <Input
                id="hris-tenant"
                value={tenantUrl}
                onChange={(e) => setTenantUrl(e.target.value)}
                placeholder="https://yourcompany.ilucca.net"
                autoComplete="off"
                className={errors.tenantUrl ? "border-error-500 focus-visible:ring-error-500" : ""}
              />
              {errors.tenantUrl && <p className="text-sm text-error-700">{errors.tenantUrl}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="hris-key">{t("hris_key_label", "API key")}</Label>
              <Input
                id="hris-key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                autoComplete="new-password"
                className={errors.apiKey ? "border-error-500 focus-visible:ring-error-500" : ""}
              />
              {errors.apiKey && <p className="text-sm text-error-700">{errors.apiKey}</p>}
            </div>
          </div>

          <KeyGuide tenantUrl={tenantUrl} />

          <div className="flex items-center justify-between gap-3 max-md:flex-col max-md:items-stretch">
            <p className="flex items-center gap-1.5 text-sm text-neutral-800">
              <ShieldCheck className="size-4 text-success-600" />
              {t("hris_key_safety", "The key is stored encrypted and is never shown again.")}
            </p>
            <div className="flex gap-2 max-md:flex-col">
              {onCancel && (
                <Button type="button" variant="outline" onClick={onCancel}>
                  {t("hris_cancel", "Cancel")}
                </Button>
              )}
              <Button type="submit" disabled={saving || !tenantUrl.trim() || !apiKey.trim()}>
                {saving && <Loader2 className="size-4 animate-spin" />}
                {t("hris_connect_btn", "Check and connect")}
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

const LUCCA_AUTH_DOCS = "https://developers.luccasoftware.com/documentation/using-api/authentication"

/**
 * Where the key comes from, step by step, with a direct link to the customer's
 * own Lucca key page once the account address is typed.
 */
function KeyGuide({ tenantUrl }: { tenantUrl: string }) {
  const t = useBookingT()
  const [open, setOpen] = React.useState(true)
  const base = /^https:\/\/[^/]+\.[a-z]+/i.test(tenantUrl.trim()) ? tenantUrl.trim().replace(/\/+$/, "") : null
  const keysUrl = base ? `${base}/identity/admin/integration-keys` : null

  const scopes = [
    t("hris_scope_employees", "Employees"),
    t("hris_scope_employments", "Employments"),
    t("hris_scope_positions", "Job positions"),
    t("hris_scope_departments", "Departments"),
    t("hris_scope_entities", "Legal entities and establishments"),
    t("hris_scope_personal", "Personal records (date of birth, sex, nationality)"),
  ]

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-md border border-neutral-200 bg-neutral-50">
      <CollapsibleTrigger asChild>
        <button type="button" className="flex w-full items-center justify-between gap-2 p-3 text-left text-sm font-medium text-neutral-900">
          <span className="flex items-center gap-2">
            <KeyRound className="size-4 text-brand-500" />
            {t("hris_guide_title", "How to get the API key (2 minutes, done by a Lucca administrator)")}
          </span>
          <ChevronDown className={open ? "size-4 rotate-180 transition-transform" : "size-4 transition-transform"} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ol className="list-decimal space-y-2 px-3 pb-3 pl-8 text-sm text-neutral-800">
          <li>
            {t("hris_guide_step1", "In Lucca, open Administration → Integration keys.")}{" "}
            {keysUrl ? (
              <a href={keysUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand-600 underline">
                {t("hris_guide_open_keys", "Open it in your Lucca account")}
                <ExternalLink className="size-3.5" />
              </a>
            ) : (
              <span className="text-neutral-800">{t("hris_guide_type_address", "Type your account address above to get a direct link.")}</span>
            )}
          </li>
          <li>
            {t("hris_guide_step2", "Create a key named “Travel Code” with read access to:")}
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {scopes.map((scope) => (
                <li key={scope}>{scope}</li>
              ))}
            </ul>
          </li>
          <li>{t("hris_guide_step3", "Copy the key and paste it here together with your account address.")}</li>
        </ol>
        <p className="px-3 pb-3 text-sm text-neutral-800">
          <a href={LUCCA_AUTH_DOCS} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand-600 underline">
            {t("hris_guide_docs", "Lucca documentation on API keys")}
            <ExternalLink className="size-3.5" />
          </a>
        </p>
      </CollapsibleContent>
    </Collapsible>
  )
}
